import React, { useState, useEffect } from 'react';

import {
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import Can from '@shared/permissions/Can';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import {
  Card,
  App,
  Button,
  Tag,
  Descriptions,
  Table,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';

export default function ImportTemplatesShow({
  importTemplate: initialImportTemplate,
  importTemplateService,
  role = 'admin',
  basePath = '/import_templates',
}) {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const [importTemplate, setImportTemplate] = useState(initialImportTemplate);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (initialImportTemplate) {
      setImportTemplate(initialImportTemplate);
    }
  }, [initialImportTemplate]);

  const handleDelete = () => {
    if (importTemplate?.deletable === false) {
      message.warning(t('common.error.permission_denied'));
      return;
    }
    modal.confirm({
      title: t('shared.import_templates.detail.delete_confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('shared.import_templates.detail.delete_confirm_description'),
      okText: t('common.button.confirm'),
      okType: 'danger',
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        setDeleting(true);
        try {
          const response = await importTemplateService.delete(importTemplate.id);
          if (response.success) {
            message.success(t('common.message.delete_success'));
            navigate(basePath, { replace: true });
          } else {
            handleApiError(response, { t, fallbackMessage: t('common.error.delete_failed') });
          }
        } catch (error) {
          handleNetworkError(error, { t });
          console.error('Delete import template error:', error);
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  if (!importTemplate) {
    return (
      <div>
        <Card>
          <p>{t('common.error.record_not_found')}</p>
        </Card>
      </div>
    );
  }

  const showEditButton = importTemplate?.editable !== false;
  const showDeleteButton = importTemplate?.deletable !== false;

  const mappingColumns = [
    {
      title: t('shared.import_templates.detail.excel_header'),
      dataIndex: 'header',
      key: 'header',
    },
    {
      title: t('shared.import_templates.detail.system_field'),
      dataIndex: 'field',
      key: 'field',
    },
  ];

  const mappingData = importTemplate.column_mappings
    ? Object.entries(importTemplate.column_mappings).map(([header, field], index) => ({
        key: index,
        header,
        field,
      }))
    : [];

  const defaultValueColumns = [
    {
      title: t('shared.import_templates.detail.field_name'),
      dataIndex: 'field',
      key: 'field',
    },
    {
      title: t('shared.import_templates.detail.default_value'),
      dataIndex: 'value',
      key: 'value',
    },
  ];

  const defaultValueData = importTemplate.default_values
    ? Object.entries(importTemplate.default_values).map(([field, value], index) => ({
        key: index,
        field,
        value: String(value),
      }))
    : [];

  const headerBasePath = role === 'operator' ? '/operator' : '/admin';

  return (
    <div>
      <ListPageHeader
        basePath={headerBasePath}
        items={['data', 'data.import_templates', { label: importTemplate.name }]}
        actions={
          <>
            {showEditButton && (
              <Can requiredPermission="import_templates:write">
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`${basePath}/${importTemplate.id}/edit`)}
                >
                  {t('common.button.edit')}
                </Button>
              </Can>
            )}
            {showDeleteButton && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleDelete}
                loading={deleting}
              >
                {t('common.button.delete')}
              </Button>
            )}
          </>
        }
      />

      <Card style={{ marginBottom: 16 }}>
        <Descriptions column={2} bordered>
          <Descriptions.Item label={t('shared.import_templates.form.name')}>
            {importTemplate.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('shared.import_templates.form.import_type')}>
            <Tag color="blue">
              {t(`common.import_type.${importTemplate.import_type}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('common.created_at')}>
            {importTemplate.created_at ? formatDateTime(importTemplate.created_at) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.updated_at')}>
            {importTemplate.updated_at ? formatDateTime(importTemplate.updated_at) : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {mappingData.length > 0 && (
        <Card title={t('shared.import_templates.detail.column_mappings')} style={{ marginBottom: 16 }}>
          <Table
            columns={mappingColumns}
            dataSource={mappingData}
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {defaultValueData.length > 0 && (
        <Card title={t('shared.import_templates.detail.default_values')}>
          <Table
            columns={defaultValueColumns}
            dataSource={defaultValueData}
            pagination={false}
            size="small"
          />
        </Card>
      )}
    </div>
  );
}
